<template>
  <v-container style="position: relative;">
    <v-col cols="12">
      <Info
        v-if="showInfo"
        :stakeholder="stakeholder"
        :project="project"
        :topic="topic"
      />
    </v-col>
    <Message
      v-for="message in messages"
      :key="message.pk"
      :sendername="senderName(message)"
      :message-text="message.body"
      :sender="isSender(message.sender_content_type.model)"
      :timestamp="message.posted_at"
      class="message"
    />
    <v-row
      class="divide"
    />
    <v-row
      v-if="conversation"
      id="input"
      :class="styleClass"
    >
      <v-col cols="10">
        <v-textarea
          id="send-field"
          ref="msgBody"
          v-model="msgBody"
          required
          :rules="messageBodyRules"
          outlined
          rows="1"
          auto-grow
          placeholder="Enter Message Here"
        />
      </v-col>
      <v-col
        cols="2"
        class="text-right"
      >
        <v-btn
          :disabled="!msgBody.trim()"
          outlined
          class="btn-format"
          @click="send()"
        >
          SEND
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Info from "@/components/Conversation/Info";
import Message from "@/components/Conversation/Message";
import { mapState, mapActions } from "vuex";
import jwt from "jwt-simple";

import rules from "@/validation.js";
import esrules from "@/esvalidation.js";

export default {
  name: "MessageRoom",
  components: {
    Info,
    Message
  },
  props: {
    stakeholder: {
      type: String,
      default: ""
    },
    project: {
      type: String,
      default: ""
    },
    topic: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showInfo: true,
      messageInterval: null,
      sender: {
        id: null,
        type: null
      },
      conversation: null,
      msgBody: "",
      rules: rules.rules,
      esrules: esrules.esrules,
      styleClass: null,
      currentContentType: null
    };
  },
  computed: {
    ...mapState({
      messages: state => state.messages.messages,
      contentTypes: state => state.messages.contentTypes,
      stakeholderNames: state => state.stakeholders.stakeholderNames,
      userNames: state => state.users.userNames,
    }),
    messageBodyRules() {
      if (this.$i18n.locale === "en") {
        return this.rules.messageBodyRules;
      } else {
        return this.esrules.messageBodyRules;
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.messageInterval);
  },
  created() {
    this.conversation = this.getCurrentConversation();
    this.loadConversation();
  },
  methods: {
    ...mapActions("messages", [
      "fetchMessages",
      "sendMessage",
      "getContentTypes"
    ]),
    ...mapActions("stakeholders", [
      "inviteStakeholderToConversation",
      "notifyStakeholder",
      "getStakeholderNames"
    ]),
    ...mapActions("users", ["notifyAdmin",
      "getUserNames"]),
    loadConversation() {
      if (this.conversation) {
        this.loadMessages()
        this.loadContentTypes()
        this.getMessages();
        this.getStakeholderNames();
        this.getUserNames();
      }
    },
    isSender(sender) {
      return sender === this.currentContentType;
    },
    loadMessages() {
      this.fetchMessages({ conversation: this.conversation.pk }).then(() => {
        this.scroll("down");
      });
    },
    loadContentTypes() {
      this.getContentTypes().then(() => {
        this.setSender();
      });
    },
    setSender() {
      const token = localStorage.getItem("TOKEN_STORAGE_KEY");
      token ? this.setToUser(token) : this.setToStakeholder();
    },
    setToUser(token) {
      this.currentContentType = "user"
      const senderType = this.contentTypes.find(ct => ct.model === "user").pk;
      const user = jwt.decode(
        token,
        process.env.VUE_APP_JWT_SECRET_KEY,
        true,
        "HS256"
      );
      this.sender.type = senderType;
      this.sender.id = user.user_id;
      this.styleClass = "userStyle";
    },
    setToStakeholder() {
      this.currentContentType = "stakeholders"
      const senderType = this.contentTypes.find(
        ct => ct.model === "stakeholders"
      ).pk;
      const stakeholder = jwt.decode(
        this.$route.query.sender,
        process.env.VUE_APP_JWT_SECRET_KEY,
        true,
        "HS256"
      );
      this.sender.type = senderType;
      this.sender.id = stakeholder.pk;
      this.styleClass = "stakeholderStyle";
      this.showInfo = false;
    },
    getCurrentConversation() {
      if (this.$route.query.conversation) {
        const conversation = jwt.decode(
          this.$route.query.conversation,
          process.env.VUE_APP_JWT_SECRET_KEY,
          true,
          "HS256"
        );
        return conversation;
      } else {
        return null;
      }
    },
    send() {
      if (!this.msgBody.trim()) {
        return;
      }
      const message = {
        conversation: this.conversation.pk,
        body: this.msgBody,
        sender_content_type: this.sender.type,
        sender_object_id: this.sender.id
      };
      this.notifyReceiver();
      this.sendMessage(message).then(() => {
        this.fetchMessages({ conversation: this.conversation.pk }).then(() => {
          this.msgBody = "";
          this.$refs.msgBody.resetValidation();
          this.scroll("down");
        });
      });
    },
    scroll(direction) {
      let scrollheight = 0;
      if (direction === "down") {
        scrollheight = document.documentElement.scrollHeight;
      }
      setTimeout(function() {
        window.scrollTo({
          top: scrollheight,
          left: 0,
          behavior: "smooth"
        });
      }, 100);
    },
    notifyReceiver() {
      if (this.messages.length === 0) {
        this.inviteStakeholderToConversation({
          stakeholder_pk: this.conversation.stakeholder.pk,
          conversation_pk: this.conversation.pk
        });
      } else {
        if (this.sender.type === "user") {
          // notify stakeholder instead
          this.notifyStakeholder({
            stakeholder_pk: this.conversation.stakeholder.pk,
            conversation_pk: this.conversation.pk
          });
        } else {
          // notify admin
          this.notifyAdmin({
            admin_pk: this.conversation.admin,
            conversation_pk: this.conversation.pk
          });
        }
      }
    },
    getMessages() {
      this.messageInterval = setInterval(() => {
        this.$store.dispatch("messages/fetchMessages", {
          conversation: this.conversation.pk
        });
      }, 5000);
    },
    senderName(message) {
      const senderType = message.sender_content_type.model;
      let name;
      if (senderType === "user") {
        const admin = this.userNames.find(
          user => user.pk === message.sender_object_id
        );
        name = admin ? admin.name : "";
      } else {
        const stakeholder = this.stakeholderNames.find(
          stakeholder => stakeholder.pk === message.sender_object_id
        );
        name = stakeholder ? stakeholder.name : "";
      }
      return name;
    }
  }
};
</script>

<style scoped>
.message {
  width: 45% !important;
}
.v-input__slot {
  width: 100% !important;
}
.layout >>> textarea#send-field {
  padding: 0% ;
  margin: 0% ;
  margin-top: 2% ;
  margin-bottom: 0.5% ;
  margin-right: 5%;
}
.layout >>> .v-input__slot {
  margin-bottom: 0px;
  background-color: white !important;
}
.btn-format {
  margin-bottom: 0;
  margin-top: 1%;
  margin-left: 3%;
  width: 80%;
}
.userStyle {
  position: fixed;
  bottom: 3% !important;
  width: 60%;
  background-color: white;
  opacity: 0.8;
}
.stakeholderStyle {
  position: fixed;
  bottom: 5%;
  width: 82.5%;
  margin-left: 5% !important;
  background-color: white;
}
.divide {
  margin-bottom: 10% !important;
}

@media (min-width: 320px) and (max-width: 1023px) {
  .stakeholderStyle {
    width: 75%;
  }
  .divide {
    margin-bottom: 37.5% !important;
  }
  .message {
    width: 75% !important;
  }
  .btn-format {
    margin-left: 0.5%;
  }
  #input.row div[data-v-65aee306] {
    width:50%;
  }
  #input.row {
    width: 70%;
    margin-left: 0% !important;
  }
}

#input {
  margin-left: 2.5%;
}

</style>
